import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useSelectedResource from '../../../hooks/useSelectedResource';
import usePageTitle from '../../../hooks/usePageTitle';
import {
  selectAuthIsAdmin,
  selectAuthIsForecaster,
  selectAuthIsMarketOwner,
  selectAuthIsMarketOwnerReadOnly,
} from '../../../slices/authSlice';
import {
  fetchMarketSessionHistory,
  fetchSessionDayList,
  selectSessionHistory,
  selectSessionHistoryLoading,
  selectSessionDayList,
  selectSessionDayListLoading,
  selectSelectedTargetDay,
  setSelectedTargetDay,
  clearSessionHistory,
  clearSessionDayList,
  clearTargetDayForecasts,
  selectModel,
  setModel,
} from '../../../slices/marketSlice';
import { selectBlurSensitive } from '../../../slices/preferencesSlice';
import { ENSEMBLE_MODELS } from '../../../constants/ensembleModels';
import { getDateParam, getStringParam, mergeSearch } from '../../../utils/urlParams';
import PermissionChecker from '../../../utils/permissionChecker';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import { useSessionDetail } from './useSessionDetail';
import { deriveDayInfo, deriveHorizonGroups } from './sessionSelectors';
import { SessionFilters } from './SessionFilters';
import { DayInfoPanel } from './DayInfoPanel';
import { ForecastsPanel } from './ForecastsPanel';
import { RecentDaysStrip } from './RecentDaysStrip';
import { IconState } from '../../../components/IconState';
import MobileBottomSheet from '../../../components/MobileBottomSheet';
import { ArrowTrendingUpIcon, ChevronRightIcon as ChevronRightIconOutline } from '@heroicons/react/24/outline';

// Local calendar date (YYYY-MM-DD) — target days are plain dates, so compare in
// the user's local zone rather than UTC.
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export default function SessionsPage() {
  usePageTitle('Forecasts');
  useBreadcrumbs([{ label: 'Forecasts' }], 'View forecasts by target day');
  const dispatch = useDispatch();

  const sessionHistory = useSelector(selectSessionHistory);
  const sessionHistoryLoading = useSelector(selectSessionHistoryLoading);
  const recentDays = useSelector(selectSessionDayList);
  const recentDaysLoading = useSelector(selectSessionDayListLoading);
  const selectedResource = useSelectedResource();
  const blurSensitive = useSelector(selectBlurSensitive);
  // Ensemble strategy (admin/MO). useSessionDetail reads this and refetches the
  // target-day forecasts whenever it changes; the picker below just sets it.
  const model = useSelector(selectModel);

  const isAdmin = useSelector(selectAuthIsAdmin);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const canViewOthersData = new PermissionChecker({
    isAdmin,
    isMarketOwner,
    isMarketOwnerReadOnly,
    isForecaster,
  }).satisfies(['market_owner', 'market_owner_read', 'admin']);

  const location = useLocation();
  const navigate = useNavigate();
  const urlSeeded = useRef(false);

  // Selected target day lives in redux so it survives navigating away and back.
  const selectedDate = useSelector(selectSelectedTargetDay);
  const setSelectedDate = useCallback((date) => dispatch(setSelectedTargetDay(date)), [dispatch]);

  // URL -> state, once on mount, before the auto-select effects fire.
  useEffect(() => {
    if (urlSeeded.current) return;
    urlSeeded.current = true;
    const day = getDateParam(location.search, 'day');
    if (day) dispatch(setSelectedTargetDay(day));
    const validModels = ENSEMBLE_MODELS.map((m) => m.value);
    const urlModel = getStringParam(location.search, 'model', { fallback: null, allowed: validModels });
    if (urlModel) dispatch(setModel({ model: urlModel }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State -> URL so the current view is shareable. replace: no history spam.
  useEffect(() => {
    if (!urlSeeded.current) return;
    const next = mergeSearch(location.search, {
      day: selectedDate || null,
      model: model || null,
    });
    if (next !== location.search) {
      navigate({ search: next }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, model]);

  // Coverage view for the selected day (every challenge covering it). The recent
  // days for the strip come from a separate fetch (selectSessionDayList) so the
  // two never clobber each other.
  const dayGroups = useMemo(() => sessionHistory?.results || [], [sessionHistory?.results]);

  // Latest target day we have forecasts for — caps the date picker (ISO dates
  // compare lexicographically; recentDays is sorted newest-first).
  const maxSelectableDate = recentDays[0]?.target_day || '';

  const selectedGroup = useMemo(
    () => dayGroups.find((g) => g.target_day === selectedDate) || null,
    [dayGroups, selectedDate]
  );
  const gates = useMemo(() => selectedGroup?.gates || [], [selectedGroup]);
  const dayInfo = useMemo(() => (selectedGroup ? deriveDayInfo(selectedGroup) : null), [selectedGroup]);
  const horizonGroups = useMemo(() => deriveHorizonGroups(gates), [gates]);

  const {
    horizonKey,
    horizonOptions,
    gateOptions,
    selectedChallengeId,
    setHorizonKey,
    setSelectedChallengeId,
    selectGate,
    targetDayForecasts,
    targetDayForecastsLoading,
    sessionRawData,
  } = useSessionDetail({ targetDay: selectedDate, gates, resourceId: selectedResource?.id });

  // Forecasters present in the selected challenge's forecasts (admin/MO only).
  const forecasters = useMemo(() => {
    const f = targetDayForecasts?.forecasts || {};
    // Only superusers receive `email` from the API. Show it to them unless
    // "Hide sensitive info" is on; everyone else sees the username alias. We
    // never blur — just swap the label.
    return Object.entries(f).map(([uid, u]) => ({
      id: uid,
      name: !blurSensitive && u.email ? u.email : u.username || uid,
    }));
  }, [targetDayForecasts, blurSensitive]);

  // Participation for the selected gate — only meaningful when the user may see
  // others' data (a forecaster sees only their own submission + no ensemble).
  const participation = useMemo(() => {
    if (!canViewOthersData) return null;
    return {
      forecasterCount: Object.keys(targetDayForecasts?.forecasts || {}).length,
      hasEnsemble: (targetDayForecasts?.ensemble?.length || 0) > 0,
    };
  }, [canViewOthersData, targetDayForecasts]);

  // null = show all forecasters; reset to "all" whenever the challenge changes.
  const [selectedForecasterIds, setSelectedForecasterIds] = useState(null);
  useEffect(() => {
    setSelectedForecasterIds(null);
  }, [selectedChallengeId]);

  // Clear the selected-day coverage on unmount so a later visit re-fetches it
  // fresh (and never shows the previous resource's data). The selected day and
  // the recent-days list intentionally persist across navigation.
  useEffect(
    () => () => {
      dispatch(clearSessionHistory());
      dispatch(clearTargetDayForecasts());
    },
    [dispatch]
  );

  // Reset the persisted selection when the resource changes mid-session.
  const prevResourceRef = useRef(selectedResource?.id);
  useEffect(() => {
    if (prevResourceRef.current && prevResourceRef.current !== selectedResource?.id) {
      dispatch(setSelectedTargetDay(null));
      dispatch(clearSessionHistory());
      dispatch(clearTargetDayForecasts());
    }
    prevResourceRef.current = selectedResource?.id;
  }, [dispatch, selectedResource?.id]);

  // Fetch the recent-days list (independent of the selected day) per resource.
  useEffect(() => {
    if (!selectedResource?.id) {
      dispatch(clearSessionDayList());
      return;
    }
    dispatch(fetchSessionDayList({ resourceId: selectedResource.id, pageSize: 60 }));
  }, [dispatch, selectedResource?.id]);

  // Fetch the selected day's coverage view once a day is chosen.
  useEffect(() => {
    if (!selectedResource?.id || !selectedDate) return;
    dispatch(
      fetchMarketSessionHistory({
        resourceId: selectedResource.id,
        page: 1,
        pageSize: 60,
        date: selectedDate,
      })
    );
  }, [dispatch, selectedResource?.id, selectedDate]);

  // Pick a default day, and correct a persisted day that doesn't belong to the
  // current resource's range (e.g. after switching resources while away).
  // recentDays is sorted by target_day descending.
  useEffect(() => {
    if (recentDaysLoading || recentDays.length === 0) return;
    const max = recentDays[0].target_day;
    const min = recentDays[recentDays.length - 1].target_day;
    if (selectedDate && selectedDate >= min && selectedDate <= max) return;
    const today = todayIso();
    const day =
      recentDays.find((g) => g.target_day === today) ||
      recentDays.find((g) => g.target_day < today) ||
      recentDays[recentDays.length - 1];
    dispatch(setSelectedTargetDay(day.target_day));
  }, [recentDays, recentDaysLoading, selectedDate, dispatch]);

  const onSelectDate = useCallback((date) => setSelectedDate(date), [setSelectedDate]);

  // Phone-only bottom sheet holding the day-info panel (gates & horizons).
  const [dayInfoSheetOpen, setDayInfoSheetOpen] = useState(false);
  const gateCount = useMemo(
    () => horizonGroups.reduce((sum, g) => sum + g.gates.length, 0),
    [horizonGroups]
  );

  if (!selectedResource?.id) {
    return (
      <div>
        <IconState
          icon={ArrowTrendingUpIcon}
          title="No resource selected"
          description="Select a resource to view its sessions."
          dashedBorder={false}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Left: this-target-day info. On lg the card is taken out of the row's
          height calculation (absolute inset-0) so the chart column defines the
          row height; the card fills it and scrolls its horizon list internally,
          keeping a constant height regardless of how many horizons a day has.
          Below lg the panel lives in a bottom sheet instead (nested scroll
          areas inside page scroll are a touch trap), opened from a summary
          button under the chart. */}
      <div className="order-3 lg:order-none col-span-12 lg:relative lg:col-span-4 min-w-0">
        <div className="hidden lg:block lg:absolute lg:inset-0">
          <DayInfoPanel
            info={dayInfo}
            horizonGroups={horizonGroups}
            selectedChallengeId={selectedChallengeId}
            onSelectGate={selectGate}
            participation={participation}
          />
        </div>

        <button
          type="button"
          onClick={() => setDayInfoSheetOpen(true)}
          className="lg:hidden w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-sm font-medium text-brand-600 dark:text-brand-400"
        >
          <span>
            Gates &amp; horizons
            {gateCount > 0 && (
              <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                {gateCount} gate{gateCount === 1 ? '' : 's'}
              </span>
            )}
          </span>
          <ChevronRightIconOutline className="h-4 w-4 opacity-60" />
        </button>

        <MobileBottomSheet
          open={dayInfoSheetOpen}
          onClose={() => setDayInfoSheetOpen(false)}
        >
          <DayInfoPanel
            info={dayInfo}
            horizonGroups={horizonGroups}
            selectedChallengeId={selectedChallengeId}
            onSelectGate={(challengeId) => {
              selectGate(challengeId);
              setDayInfoSheetOpen(false);
            }}
            participation={participation}
          />
        </MobileBottomSheet>
      </div>

      {/* Right: chip filter bar + forecast chart. First thing (after the day
          strip) on phones. */}
      <div className="order-2 lg:order-none col-span-12 lg:col-span-8 flex flex-col gap-4 min-w-0">
        <SessionFilters
          selectedDate={selectedDate}
          maxDate={maxSelectableDate}
          onDateChange={onSelectDate}
          horizonOptions={horizonOptions}
          horizonKey={horizonKey}
          onHorizonChange={setHorizonKey}
          gateOptions={gateOptions}
          selectedChallengeId={selectedChallengeId}
          onGateChange={setSelectedChallengeId}
          forecasters={canViewOthersData ? forecasters : []}
          selectedForecasterIds={selectedForecasterIds}
          onForecastersChange={setSelectedForecasterIds}
          model={model}
          onModelChange={(m) => dispatch(setModel({ model: m }))}
          showModelPicker={canViewOthersData}
        />

        {selectedDate && !selectedGroup && !sessionHistoryLoading && (
          <div className="text-xs text-gray-500 dark:text-gray-400">No sessions for this day.</div>
        )}

        <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 p-4 shadow-sm">
          <ForecastsPanel
            targetDayForecasts={targetDayForecasts}
            sessionRawData={sessionRawData}
            canViewOthersData={canViewOthersData}
            blurSensitive={blurSensitive}
            selectedForecasterIds={selectedForecasterIds}
            loading={targetDayForecastsLoading}
            units={selectedResource?.units}
          />
        </div>
      </div>

      {/* Recent target days for quick scanning + switching. On phones this is
          the primary day picker, so it leads the page. */}
      <div className="order-1 lg:order-none col-span-12 mb-0 lg:mb-4 min-w-0">
        <RecentDaysStrip dayGroups={recentDays} selectedDate={selectedDate} onSelect={onSelectDate} />
      </div>
    </div>
  );
}
